import { ALL_DATES } from '@utils/cbDateSettings'

/**
 * Report filter values → the wire format GenerateReportData expects.
 * A plain module (not part of ReportView.jsx) so tests can import it and
 * react-refresh keeps working — the repo rule for shared non-component code.
 *
 * The contract below is V1's EXACT wire format, read from the legacy sources
 * (ReportsView.js `makeFilters` + Report.cshtml + ReportHelper.MakeSQLParameters):
 *
 *  - DROPDOWN — the jqx multi-select. "Select All" checked → the literal
 *    "$$cb_all" sentinel; otherwise the checked keys PIPE-joined with a
 *    TRAILING pipe ("1|", "1|2|"). Nothing checked → the filter is omitted.
 *  - CHECKBOX / ACCOUNTSDROPDOWN / TAGMANAGER — pipe-joined values with a
 *    trailing pipe; nothing selected → omitted. V1 NEVER sends "$$cb_all" for
 *    these: proc-based reports bind them to nvarchar params whose procs split
 *    on '|' and CAST each piece to int, so the sentinel throws QA's
 *    "Conversion failed when converting the varchar value '$$cb_all' to data
 *    type int" (CC-49871). Omitting the filter is what V1 does — the proc
 *    falls back to its own default ("all").
 *  - RADIO — exactly one bare key ("None" when nothing is checked — V1 posts
 *    the literal string, never the sentinel and never an omission).
 *  - LOOKUP — one bare object id. DATE — "from|to"; All Dates depends on the
 *    REPORT type, exactly like ReportsView.makeFilters: BASIC reports send
 *    "$$cb_all", proc-based reports send "|" (MakeSQLParameters turns the two
 *    empty halves into SQLDateMin/Max). OMITTING the filter is NOT equivalent:
 *    MakeSQLParameters only creates the @min/@max SqlParameters when the
 *    filter is present, and procs without defaults then throw "expects
 *    parameter '@minDate'" (CC-49923, Check Reconciliation Status 192).
 *    NUMBER — ONE bare number (spec 184). NUMBERPICKER — "<code>|v1|v2" with
 *    CB's numeric operator code (spec 184). TEXT/BOOL/HIDDEN — raw.
 *
 * Two default-value quirks CB definitions carry (CC-49897):
 *  - Multi-value dropdown defaults are COMMA-separated ("1,2" — Report.cshtml's
 *    isSelected splits on ','). V1 re-emits them pipe-joined on the wire; the
 *    raw comma string reaching SQL is the "'1,2' to data type int" crash.
 *  - FilterDefault "ALL" is a sentinel meaning "everything selected" (V1 checks
 *    the jqx Select-All for it) — never a literal value.
 */

const DROPDOWN_FAMILY = new Set(['DROPDOWN', 'CHECKBOX', 'ACCOUNTSDROPDOWN'])

// Value tokens are stored as "id" or "id:displayName" (CC-49866); the server
// gets ids only.
export const idOf = (part) => (part.includes(':') ? part.slice(0, part.indexOf(':')) : part)

/*
 * Spec 164 — the "current user" macro, and why it must never reach CB on a LOOKUP.
 *
 * A report definition can default a LOOKUP filter to `$$cb_userid` (vladdemo: 121
 * Timekeeper, 281 Responsible Attorney, 226 Scheduled For). V1 never posts it:
 * ReportsController.FixFilters rewrites it to "<UserId>:<UserName>" before the page
 * renders, and the lookup posts the id. CB's LOOKUP binder `ParseGuid`s the value
 * (ReportHelper.cs:1303), so the macro, or any "id:name" string, binds as NULL, which
 * the proc reads as "every timekeeper". Measured on 121, 2026-10-13: the macro and
 * "<id>:Admin" both returned 914 rows / 77 timekeepers, the same as no filter; the
 * bare id returned 107 rows / 1 timekeeper.
 *
 * cb-api now resolves the default for the verified caller (reportDefinition.js
 * `resolveCurrentUserFilterDefaults`). A value stored before that still holds the
 * macro, so the wire swaps it for the id cb-api resolved for that column's default.
 * When that default carries no id (an older worker), the macro is dropped rather than
 * sent: CB reads both the same way, and a value that cannot bind is not a filter.
 */
export const CURRENT_USER_MACRO = '$$cb_userid'
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i
const resolvedCurrentUserId = (col) => {
  const id = idOf(String(col?.filterDefault ?? '').split('|')[0].trim())
  return GUID_RE.test(id) ? [id] : []
}

// "All" spellings a dropdown-family value can carry: the CB sentinel, or the
// definition-default literal "ALL". Scoped to the dropdown family + RADIO —
// a TEXT filter the user typed "ALL" into is a real value.
export const isAllSentinel = (v) => {
  const s = String(v ?? '').trim()
  return s === ALL_DATES || s.toUpperCase() === 'ALL'
}

// Multi-value split: pipes are our (and V1's) separator; commas are CB's
// DEFAULT-value format (CC-49897) — but only numeric key lists ("1,2") use
// them, and real option values (names) can legitimately contain commas, so
// the comma split applies only to pure digit/comma strings.
export const splitMultiValue = (v) => {
  const s = String(v ?? '')
  const parts = !s.includes('|') && /^[\d\s,]+$/.test(s) ? s.split(',') : s.split('|')
  return parts.map((x) => x.trim()).filter(Boolean)
}

// A report may declare its own "All" condition (e.g. Trust Balance 0=Has
// Balance / 1=All) — that key IS the wire value V1 posts for All (CC-49867).
export const declaredAllKey = (col) => {
  const all = (col?.filterConditions || []).find(
    (c) => String(c.value ?? '').trim().toLowerCase() === 'all'
  )
  return all ? String(all.key) : null
}

/*
 * Spec 184 (CC-54222) — NUMBER filters carry ONE bare number, never an operator.
 *
 * CB reads a NUMBER filter in exactly one way: MakeSQLParameters hands the whole
 * value to `ParseInteger(value, -1)` (INTEGER columns) or `ParseDecimal(value, 0)`
 * (DECIMAL) — ReportHelper.cs, `case ReportFilterTypes.NUMBER`. There is no operator
 * anywhere on that path, and V1 renders a plain `<input type="number">`
 * (Report.cshtml `case NUMBER`) whose value makeFilters posts as-is.
 *
 * cb2 used to render NUMBER with the NUMBERPICKER's operator control, so any edit
 * stored "<old value>|N" (typing in the box) or "IsEqualTo|N" (touching the
 * operator). Neither parses, so CB ran the proc with -1. Measured on vladdemo
 * 2026-10-15, report 52 "Top % Grouping": "IsEqualTo|50", "80|50", "IsGreaterThan|50"
 * and "50.5" all returned the SAME 200 result as an explicit "-1" — groups headed
 * "Matters Contributing -1% / 101% of Fees Received" — while a bare "50" returned
 * one 31-matter group. On report 329 the same -1 selects zero clients and the run
 * never completes.
 *
 * This reads the number a value holds, for the control AND the wire, so the box and
 * the run cannot disagree:
 *  - a bare number is itself;
 *  - a value stored by the old control is the number that was in its value box
 *    ("<anything>|N" → N) — persisted filters, saved views and "Run again" job
 *    parameters made before the fix still hold that shape. The operator is dropped:
 *    CB never had one, and every such value ran as -1 before;
 *  - nothing readable (an operator with an empty box, "abc", "1e3") is '' — no
 *    number, which the wire omits exactly as V1 omits an empty box;
 *  - on an INTEGER column a fraction is truncated ("2.5" → "2") and a value outside
 *    Int32 is unreadable: either would otherwise reach ParseInteger and become -1.
 */
const PLAIN_NUMBER_RE = /^-?(\d+\.?\d*|\.\d+)$/
const INT32_MAX = 2147483647

export const numberFilterValue = (value, col) => {
  const s = String(value ?? '').trim()
  if (s === '') return ''
  const n = (s.includes('|') ? s.split('|')[1] : s)?.trim() ?? ''
  if (!PLAIN_NUMBER_RE.test(n)) return ''
  if (col?.dataType !== 'INTEGER') return n
  const int = Math.trunc(Number(n))
  if (!Number.isFinite(int) || Math.abs(int) > INT32_MAX) return ''
  return String(int)
}

/*
 * Spec 184 — NUMBERPICKER operators, as CB numbers them.
 *
 * Only BASIC reports read a NUMBERPICKER (BasicReport_Filter "Handle NumberPicker";
 * MakeSQLParameters has no case for it, so a proc report ignores one in V1 as well).
 * The operator is `ParseEnumerator<SearchOperatorType>(parts[0], Equals)`, i.e.
 * `Enum.Parse` on CB's own names (MesaUtilsBase.cs ParseEnumerator) — and cb2's names
 * ("IsLessThan", "IsGreaterThan", …) are not CB's ("LessThan", "GreatherThan" — sic),
 * so every one of them silently became "=". V1's CBNumberPicker.js posts the numeric
 * codes instead; these are CB's SearchOperatorType values
 * (DataTransferObjectsSearch.cs) and the codes CBNumberPicker.js offers.
 *
 * The panel keeps cb2's names (they are what the filter chip has always shown); the
 * wire translates. `takes` = how many numbers the operator needs; a value without
 * them is incomplete and is not sent — "> (empty)" used to reach CB as the bare
 * operator, which its one-part branch parses with an IsEmpty default.
 * Latent on vladdemo: no report there has a filterable NUMBERPICKER (measured
 * 2026-10-15, all 140 definitions).
 */
export const NUMBER_PICKER_OPERATORS = [
  { name: 'IsEqualTo', code: '0', label: '=', takes: 1 },
  { name: 'IsNotEqualTo', code: '1', label: '≠', takes: 1 },
  { name: 'IsLessThan', code: '3', label: '<', takes: 1 },
  { name: 'IsLessThanOrEqualTo', code: '5', label: '≤', takes: 1 },
  { name: 'IsGreaterThan', code: '2', label: '>', takes: 1 },
  { name: 'IsGreaterThanOrEqualTo', code: '4', label: '≥', takes: 1 },
  { name: 'IsBetween', code: '22', label: 'Between', takes: 2 },
  { name: 'IsEmpty', code: '6', label: 'Is Empty', takes: 0 },
  { name: 'IsNotEmpty', code: '7', label: 'Is Not Empty', takes: 0 },
]

/** The operator a stored NUMBERPICKER value names — by cb2 name or CB code — or null. */
export const numberPickerOperator = (token) => {
  const t = String(token ?? '').trim()
  return NUMBER_PICKER_OPERATORS.find((o) => o.name === t || o.code === t) || null
}

const numberPickerWire = (value) => {
  const parts = String(value).split('|').map((p) => p.trim())
  const op = numberPickerOperator(parts[0])
  if (!op) return null
  const nums = parts.slice(1, 1 + op.takes)
  if (nums.length < op.takes || !nums.every((n) => PLAIN_NUMBER_RE.test(n))) return null
  return [op.code, ...nums].join('|')
}

// ReportTypes.BASIC = 1 (tolerate a string-serialized enum). BASIC reports
// filter via expressions; everything else runs stored procs whose parameters
// only exist when the filter is on the wire.
const isBasicReport = (reportType) =>
  reportType === 1 || reportType === '1' || String(reportType).toUpperCase() === 'BASIC'

export const filtersAsArray = (valuesById, columns = [], reportType) => {
  const colById = new Map(columns.map((c) => [String(c.columnId), c]))
  const out = []
  for (const [cid, rawValue] of Object.entries(valuesById)) {
    if (rawValue == null || rawValue === '') continue
    const col = colById.get(String(cid))
    const t = col?.filterTypeName
    const value = String(rawValue)

    let wire
    if (DROPDOWN_FAMILY.has(t)) {
      const tokens = splitMultiValue(value).filter((p) => !isAllSentinel(p))
      if (tokens.length === 0) {
        // "All": a declared All condition keeps its own key (pipe-terminated,
        // like any checked jqx item); otherwise DROPDOWN sends V1's bare
        // sentinel and CHECKBOX/ACCOUNTSDROPDOWN omit the filter entirely.
        const allKey = declaredAllKey(col)
        if (allKey != null) wire = `${allKey}|`
        else if (t === 'DROPDOWN') wire = ALL_DATES
        else continue
      } else {
        wire = `${tokens.map(idOf).join('|')}|`
      }
    } else if (t === 'TAGMANAGER') {
      // Split on pipes only — "id:name" parts can carry commas in the name.
      // A stale sentinel id (pre-fix persisted value) is dropped, not sent.
      const ids = String(value).split('|').filter(Boolean).map(idOf).filter((p) => !isAllSentinel(p))
      if (ids.length === 0) continue
      wire = `${ids.join('|')}|`
    } else if (t === 'LOOKUP') {
      // Spec 164: a stored `$$cb_userid` (persisted before cb-api resolved it, or a
      // saved run's filters) becomes the id cb-api resolved for THIS column's default.
      const ids = String(value).split('|').filter(Boolean).map(idOf)
        .flatMap((p) => (p === CURRENT_USER_MACRO ? resolvedCurrentUserId(col) : [p]))
        .filter((p) => !isAllSentinel(p))
      if (ids.length === 0) continue
      wire = ids.join('|')
    } else if (t === 'RADIO') {
      // Radios always post one real key; an "All" sentinel here can only be a
      // stale persisted value — the declared All key covers it, else V1's
      // unchecked-radio literal "None" (ParseInteger → -1 = report default;
      // never the bare sentinel: int-typed keys, and never an omission:
      // the proc's parameter must exist).
      if (isAllSentinel(value)) {
        const allKey = declaredAllKey(col)
        wire = allKey ?? 'None'
      } else {
        wire = splitMultiValue(value)[0]
      }
    } else if (t === 'NUMBER') {
      // Spec 184: the bare number, or nothing — never an operator CB reads as -1.
      wire = numberFilterValue(value, col)
      if (wire === '') continue
    } else if (t === 'NUMBERPICKER') {
      // Spec 184: CB's numeric operator code; an incomplete value is not sent.
      wire = numberPickerWire(value)
      if (wire == null) continue
    } else if (t === 'DATE') {
      // All Dates is NOT omitted — V1 always posts the filter (makeFilters):
      // "$$cb_all" on BASIC reports, "|" on proc reports so MakeSQLParameters
      // creates the @min/@max params as SQLDateMin/Max (CC-49923).
      wire = value === ALL_DATES ? (isBasicReport(reportType) ? ALL_DATES : '|') : value
    } else {
      // The sentinel on any other type (SIMPLEDATE, TEXT, BOOL, …) is a stale
      // value — omit, matching V1's empty-input behavior.
      if (value === ALL_DATES) continue
      wire = value
    }

    out.push({ columnId: parseInt(cid, 10), value: wire })
  }
  return out
}
